/**
 * Student Signup Page - עמוד הרשמת תלמידים
 * Free student account for the school learning tool, joined via a class code.
 * No date-of-birth / national ID (may be minors). Educational tool only.
 */

import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { GraduationCap, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react'
import PasswordStrengthIndicator from '../components/auth/PasswordStrengthIndicator'
import api, { handleApiError } from '../services/api'

const studentSchema = z.object({
  firstName: z.string().min(2, 'שם פרטי חייב להכיל לפחות 2 תווים').max(50, 'שם פרטי ארוך מדי'),
  lastName: z.string().min(2, 'שם משפחה חייב להכיל לפחות 2 תווים').max(50, 'שם משפחה ארוך מדי'),
  email: z.string().email('כתובת אימייל לא תקינה').min(1, 'שדה חובה'),
  password: z
    .string()
    .min(8, 'הסיסמה חייבת להכיל לפחות 8 תווים')
    .regex(/[A-Za-z֐-׿]/, 'הסיסמה חייבת להכיל לפחות אות אחת')
    .regex(/[0-9]/, 'הסיסמה חייבת להכיל לפחות ספרה אחת'),
  confirmPassword: z.string().min(1, 'שדה חובה'),
  classCode: z
    .string()
    .min(3, 'קוד כיתה קצר מדי')
    .max(32, 'קוד כיתה ארוך מדי')
    .regex(/^[A-Za-z0-9-]+$/, 'קוד כיתה יכול להכיל אותיות באנגלית, ספרות ומקף בלבד'),
  parentalConsentAck: z.boolean().optional(),
  privacyPolicyAccepted: z.boolean().refine((v) => v === true, {
    message: 'חובה לקבל את תנאי השימוש ומדיניות הפרטיות',
  }),
}).refine((d) => d.password === d.confirmPassword, {
  message: 'הסיסמאות אינן תואמות',
  path: ['confirmPassword'],
})

type StudentFormData = z.infer<typeof studentSchema>

export default function StudentSignupPage() {
  const { code: codeParam } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [classPreview, setClassPreview] = useState<{ valid: boolean; label?: string; seatsRemaining?: number; reason?: string } | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<StudentFormData>({
    resolver: zodResolver(studentSchema),
    defaultValues: { classCode: (codeParam || '').toUpperCase(), privacyPolicyAccepted: false },
  })

  const password = watch('password')
  const classCode = watch('classCode')

  useEffect(() => {
    if (codeParam) setValue('classCode', codeParam.toUpperCase())
  }, [codeParam, setValue])

  // Live class-code preview (debounced).
  useEffect(() => {
    const code = (classCode || '').trim()
    if (code.length < 3) {
      setClassPreview(null)
      return
    }
    const t = setTimeout(() => {
      api
        .get(`/auth/class-code/${encodeURIComponent(code)}`)
        .then((res) => setClassPreview(res.data))
        .catch(() => setClassPreview({ valid: false, reason: 'קוד כיתה לא תקין' }))
    }, 400)
    return () => clearTimeout(t)
  }, [classCode])

  const onSubmit = async (data: StudentFormData) => {
    setIsLoading(true)
    setError(null)
    try {
      await api.post('/auth/register-student', {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        classCode: data.classCode.toUpperCase(),
        parentalConsentAck: data.parentalConsentAck || false,
        privacyPolicyAccepted: data.privacyPolicyAccepted,
      })
      setSuccess(true)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircle size={32} className="text-green-600" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">ההרשמה הושלמה!</h2>
            <p className="text-gray-600 mb-2">שלחנו אליך אימייל לאימות כתובת הדוא"ל.</p>
            <p className="text-gray-600 mb-4">אנא בדוק/י את תיבת הדואר (כולל ספאם) ולחצ/י על קישור האימות.</p>
            <p className="text-sm text-gray-500">לאחר האימות תוכל/י להתחבר ולהתחיל ללמוד.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-xl my-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-100 text-primary-700 rounded-full mb-3">
            <GraduationCap size={28} aria-hidden="true" />
          </div>
          <h1 className="text-3xl font-heading font-bold text-primary-600 mb-1">מדריך ההשקעות הלימודי</h1>
          <p className="text-gray-600">הרשמת תלמידים — חינם, דרך קוד כיתה</p>
        </div>

        <div className="card">
          <p className="text-sm text-gray-600 mb-6 bg-blue-50 border border-blue-200 rounded-lg p-3">
            זהו כלי חינוכי ללימוד עולם ההשקעות. הוא מסביר ומלמד בלבד — אינו נותן ייעוץ השקעות אישי.
          </p>

          {error && (
            <div className="alert alert-error mb-6" role="alert">
              <AlertCircle size={20} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Class code */}
            <div>
              <label htmlFor="classCode" className="block text-sm font-medium text-gray-700 mb-2">
                קוד כיתה <span className="text-red-500">*</span>
              </label>
              <input
                {...register('classCode')}
                type="text"
                id="classCode"
                className={`input ${errors.classCode ? 'input-error' : ''}`}
                placeholder="SCHOOL-9A"
                dir="ltr"
                aria-required="true"
              />
              {errors.classCode && (
                <p role="alert" className="text-sm text-red-600 mt-1">{errors.classCode.message}</p>
              )}
              {classPreview && !errors.classCode && (
                classPreview.valid ? (
                  <p className="text-sm text-green-700 mt-1">
                    ✓ {classPreview.label}
                    {typeof classPreview.seatsRemaining === 'number' ? ` — נותרו ${classPreview.seatsRemaining} מקומות` : ''}
                  </p>
                ) : (
                  <p className="text-sm text-red-600 mt-1">{classPreview.reason || 'קוד כיתה לא תקין'}</p>
                )
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  שם פרטי <span className="text-red-500">*</span>
                </label>
                <input {...register('firstName')} type="text" id="firstName" className={`input ${errors.firstName ? 'input-error' : ''}`} aria-required="true" />
                {errors.firstName && <p role="alert" className="text-sm text-red-600 mt-1">{errors.firstName.message}</p>}
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  שם משפחה <span className="text-red-500">*</span>
                </label>
                <input {...register('lastName')} type="text" id="lastName" className={`input ${errors.lastName ? 'input-error' : ''}`} aria-required="true" />
                {errors.lastName && <p role="alert" className="text-sm text-red-600 mt-1">{errors.lastName.message}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                אימייל <span className="text-red-500">*</span>
              </label>
              <input {...register('email')} type="email" id="email" className={`input ${errors.email ? 'input-error' : ''}`} dir="ltr" autoComplete="email" aria-required="true" />
              {errors.email && <p role="alert" className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                סיסמה <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input {...register('password')} type={showPassword ? 'text' : 'password'} id="password" className={`input ${errors.password ? 'input-error' : ''}`} dir="ltr" autoComplete="new-password" aria-required="true" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700" aria-label={showPassword ? 'הסתרת סיסמה' : 'הצגת סיסמה'}>
                  {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                </button>
              </div>
              {errors.password && <p role="alert" className="text-sm text-red-600 mt-1">{errors.password.message}</p>}
              <PasswordStrengthIndicator password={password || ''} />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                אימות סיסמה <span className="text-red-500">*</span>
              </label>
              <input {...register('confirmPassword')} type={showPassword ? 'text' : 'password'} id="confirmPassword" className={`input ${errors.confirmPassword ? 'input-error' : ''}`} dir="ltr" autoComplete="new-password" aria-required="true" />
              {errors.confirmPassword && <p role="alert" className="text-sm text-red-600 mt-1">{errors.confirmPassword.message}</p>}
            </div>

            {/* Consents */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
              <div className="flex items-start gap-3">
                <input {...register('privacyPolicyAccepted')} type="checkbox" id="privacyPolicyAccepted" className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" aria-required="true" />
                <label htmlFor="privacyPolicyAccepted" className="text-sm text-gray-700 flex-1">
                  <span className="text-red-500">*</span> קראתי והסכמתי ל
                  <Link to="/terms" target="_blank" className="text-primary-600 hover:text-primary-700 underline mx-1">תנאי השימוש</Link>
                  ול
                  <Link to="/privacy" target="_blank" className="text-primary-600 hover:text-primary-700 underline mx-1">מדיניות הפרטיות</Link>.
                </label>
              </div>
              {errors.privacyPolicyAccepted && <p role="alert" className="text-sm text-red-600">{errors.privacyPolicyAccepted.message}</p>}

              <div className="flex items-start gap-3">
                <input {...register('parentalConsentAck')} type="checkbox" id="parentalConsentAck" className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
                <label htmlFor="parentalConsentAck" className="text-sm text-gray-700 flex-1">
                  אני מאשר/ת שהורה או מורה מודע/ת להרשמה שלי לכלי הלימודי (מומלץ לתלמידים מתחת לגיל 18).
                </label>
              </div>
            </div>

            <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
              {isLoading ? (
                <div className="flex items-center justify-center gap-2"><div className="spinner" /><span>נרשם...</span></div>
              ) : (
                <div className="flex items-center justify-center gap-2"><GraduationCap size={20} aria-hidden="true" /><span>הרשמה חינם</span></div>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              כבר יש לך חשבון?{' '}
              <Link to="/student-login" className="text-primary-600 hover:text-primary-700 font-medium">התחבר כאן</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
